import React, { useState, useEffect } from 'react';
import { Tabs, Card, Button, Input, message, Spin } from 'antd';
import { SaveOutlined, FileTextOutlined, SafetyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const { TabPane } = Tabs;

const DEFAULT_TERMS = `TERMS AND CONDITIONS

Effective Date: June 3, 2026
Last Updated: June 3, 2026

Welcome to GuffPuff! These Terms and Conditions ("Terms") govern your access to and use of the GuffPuff mobile application and related services ("Service") operated by GuffPuff LLC ("Company", "we", "us", or "our"). By downloading, installing, or using the GuffPuff app, you agree to be bound by these Terms.

1. ACCEPTANCE OF TERMS

By accessing or using our Service, you confirm that you are at least 18 years of age (or the age of majority in your jurisdiction), that you have read and understood these Terms, and that you agree to be legally bound by them. If you do not agree, please do not use GuffPuff.

2. DESCRIPTION OF SERVICE

GuffPuff is a social voice and chat platform that allows users to create and join live audio conversations, send direct messages, explore interest-based categories, and connect with other users. We offer both free and premium subscription tiers with different features and limits.

3. USER ACCOUNTS

3.1 Registration: You must create an account to use GuffPuff. You agree to provide accurate, current, and complete information during registration and to keep your account information updated.

3.2 Account Security: You are responsible for maintaining the confidentiality of your login credentials. You must notify us immediately at support@guffpuff.com if you suspect any unauthorized access to your account.

3.3 One Account Per User: You may not create multiple accounts. Duplicate accounts may be terminated without notice.

4. ACCEPTABLE USE POLICY

You agree NOT to use GuffPuff to:
- Post, share, or transmit content that is abusive, harassing, threatening, defamatory, or hateful
- Impersonate any person or entity or misrepresent your affiliation with any person or entity
- Share explicit, pornographic, or sexually inappropriate content
- Distribute spam, unsolicited messages, or promotional content without permission
- Engage in any illegal activity or encourage others to do so
- Attempt to hack, reverse engineer, or disrupt the platform or its servers
- Collect or harvest personal data of other users without consent

Violation of this policy may result in immediate account suspension or permanent ban.

5. CONTENT OWNERSHIP

5.1 Your Content: You retain ownership of any content you create and share on GuffPuff. By posting content, you grant GuffPuff a non-exclusive, worldwide, royalty-free license to use, store, reproduce, and display that content solely for the purpose of operating the Service.

5.2 Our Content: All design elements, logos, branding, and proprietary features of GuffPuff are the intellectual property of GuffPuff LLC. You may not copy, modify, or distribute them without written permission.

6. SUBSCRIPTIONS AND PAYMENTS

6.1 Free Tier: GuffPuff offers a free tier with limited features. Details of free-tier limits are available in the app.

6.2 Premium Subscription: Paid plans unlock additional features including extended conversation time, higher-quality audio, exclusive categories, and priority support. Subscription fees are billed monthly or annually as selected at checkout.

6.3 Cancellation: You may cancel your subscription at any time. Your access to premium features will continue until the end of the current billing period. No refunds are issued for partial periods.

6.4 Promo Codes: Promotional discount codes are single-use, non-transferable, and subject to expiry dates. GuffPuff reserves the right to revoke promo codes at any time.

7. PRIVACY

Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, use, and protect your personal data. By using GuffPuff, you agree to the practices described in our Privacy Policy.

8. TERMINATION

We reserve the right to suspend or permanently terminate your account at our sole discretion if you violate these Terms or engage in conduct that we determine is harmful to the platform, other users, or GuffPuff LLC. You may also delete your account at any time from the app settings.

9. DISCLAIMERS

GuffPuff is provided "as is" without warranties of any kind, express or implied. We do not guarantee uninterrupted or error-free service. We are not responsible for content shared by users on the platform.

10. LIMITATION OF LIABILITY

To the maximum extent permitted by applicable law, GuffPuff LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.

11. CHANGES TO TERMS

We may update these Terms at any time. We will notify registered users of material changes via in-app notification or email. Continued use of GuffPuff after changes constitutes your acceptance of the revised Terms.

12. GOVERNING LAW

These Terms are governed by the laws of the jurisdiction in which GuffPuff LLC is registered, without regard to conflict of law provisions.

13. CONTACT US

If you have any questions about these Terms, please contact us:
Email: legal@guffpuff.com
Support: support@guffpuff.com
Website: www.guffpuff.com`;

const DEFAULT_PRIVACY = `PRIVACY POLICY

Effective Date: June 3, 2026
Last Updated: June 3, 2026

GuffPuff LLC ("GuffPuff", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains what personal information we collect, how we use it, and your rights regarding your data when you use the GuffPuff mobile application ("App") and related services.

By using GuffPuff, you agree to the collection and use of information as described in this Policy.

1. INFORMATION WE COLLECT

1.1 Information You Provide:
- Account Information: When you register, we collect your name, email address, date of birth, and password.
- Profile Information: Profile photo, username, bio, and interests you choose to add.
- Communications: Messages, audio content, and other content you share on GuffPuff.
- Support Requests: When you contact our support team, we retain records of that correspondence.

1.2 Information Collected Automatically:
- Device Information: Device type, operating system version, unique device identifiers, and mobile network information.
- Usage Data: Features you use, conversations you join, duration of sessions, and interaction patterns within the app.
- Log Data: IP address, app crash reports, timestamps, and diagnostic data.
- Location Data: Approximate location derived from IP address (we do not collect precise GPS location unless you explicitly grant permission).

1.3 Information from Third Parties:
- If you connect GuffPuff to third-party accounts (e.g., Google, Apple), we receive basic profile information from those services as permitted by your settings with them.

2. HOW WE USE YOUR INFORMATION

We use the information we collect to:
- Create and manage your account
- Provide, operate, and improve the GuffPuff platform
- Personalize your experience and recommend relevant categories and conversations
- Process subscription payments and manage promo codes
- Send you service notifications, updates, and (where permitted) promotional communications
- Monitor for and prevent fraud, abuse, and violations of our Terms and Conditions
- Comply with legal obligations
- Conduct internal analytics and research to improve our services

3. HOW WE SHARE YOUR INFORMATION

We do not sell your personal data. We may share your information only in the following circumstances:

3.1 With Other Users: Your username, profile photo, and public content you post are visible to other GuffPuff users.

3.2 Service Providers: We work with trusted third-party vendors (cloud hosting, payment processors, analytics providers) who process data on our behalf under strict confidentiality agreements.

3.3 Legal Requirements: We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect the safety of any person.

3.4 Business Transfers: In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction. We will notify you via email or in-app notice before your data is subject to a different privacy policy.

4. DATA RETENTION

We retain your personal data for as long as your account is active or as needed to provide you with our services. If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law or for legitimate business purposes (such as fraud prevention records).

5. YOUR RIGHTS

Depending on your location, you may have the following rights:
- Access: Request a copy of the personal data we hold about you.
- Correction: Request correction of inaccurate or incomplete data.
- Deletion: Request deletion of your personal data ("right to be forgotten").
- Portability: Request your data in a machine-readable format.
- Objection: Object to certain types of data processing.
- Withdraw Consent: Where processing is based on consent, you may withdraw it at any time.

To exercise any of these rights, please contact us at privacy@guffpuff.com.

6. COOKIES AND TRACKING

The GuffPuff app does not use browser cookies. However, we use similar tracking technologies (such as device identifiers and analytics SDKs) to understand how users interact with the app and to improve performance. You may opt out of analytics tracking in the app settings.

7. CHILDREN'S PRIVACY

GuffPuff is not intended for children under the age of 13 (or 16 in certain jurisdictions). We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal data, we will promptly delete it. If you believe we have inadvertently collected data from a child, please contact us at privacy@guffpuff.com.

8. SECURITY

We implement industry-standard security measures to protect your personal data, including encryption in transit (TLS), encrypted storage, and access controls. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.

9. THIRD-PARTY LINKS

GuffPuff may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties and encourage you to review their privacy policies independently.

10. CHANGES TO THIS POLICY

We may update this Privacy Policy from time to time. We will notify you of significant changes via in-app notification or by email. The "Last Updated" date at the top of this page reflects the most recent revision. Your continued use of GuffPuff after changes are posted constitutes your acceptance of the revised Policy.

11. CONTACT US

If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:

Privacy Team: privacy@guffpuff.com
General Support: support@guffpuff.com
GuffPuff LLC
Website: www.guffpuff.com`;

const DEFAULT_SAFETY = `SAFETY & PRIVACY

Effective Date: June 3, 2026
Last Updated: June 3, 2026

At GuffPuff, your safety and privacy come first. This page explains the tools, practices, and guidelines we have in place to help keep you safe while using GuffPuff.

1. OUR COMMITMENT TO SAFETY

We are committed to providing a respectful and secure environment for all users. We continuously work to prevent harassment, abuse, and harmful behavior on the platform.

2. STAYING SAFE ON GUFFPUFF

- Never share sensitive personal information (home address, financial details, passwords) with other users.
- Be cautious when interacting with people you do not know.
- Use a strong, unique password and keep your login credentials private.
- Report any user or content that makes you feel unsafe.

3. BLOCKING AND REPORTING

You can block any user at any time to stop them from contacting you. You can also report users or content that violate our community guidelines. Our moderation team reviews reports and takes appropriate action, which may include warnings, suspensions, or permanent bans.

4. PRIVACY CONTROLS

You control what you share. In your settings you can manage profile visibility, who can message you, and what information is shown to other users.

5. PROTECTING MINORS

GuffPuff is intended for adults. We do not allow users under the age of 18. If you believe a minor is using the platform, please report it immediately.

6. CONTACT US

If you have any safety or privacy concerns, please reach out to us:
Safety Team: safety@guffpuff.com
Privacy Team: privacy@guffpuff.com
Support: support@guffpuff.com
Website: www.guffpuff.com`;

const LegalContentManagement = () => {
  const { logActivity } = useAdminAuth();

  const [termsTitle, setTermsTitle] = useState('Terms and Conditions');
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(true);
  const [termsSaving, setTermsSaving] = useState(false);

  const [privacyTitle, setPrivacyTitle] = useState('Privacy Policy');
  const [privacyContent, setPrivacyContent] = useState('');
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [privacySaving, setPrivacySaving] = useState(false);

  const [safetyTitle, setSafetyTitle] = useState('Safety & Privacy');
  const [safetyContent, setSafetyContent] = useState('');
  const [safetyLoading, setSafetyLoading] = useState(true);
  const [safetySaving, setSafetySaving] = useState(false);

  useEffect(() => {
    fetchTerms();
    fetchPrivacy();
    fetchSafety();
    logActivity('view_legal_content', 'Viewed Legal Content Management page');
  }, []);

  const fetchTerms = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'legal_content', 'terms_and_conditions'));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setTermsTitle(data.title || 'Terms and Conditions');
        setTermsContent(data.content || DEFAULT_TERMS);
      } else {
        setTermsContent(DEFAULT_TERMS);
      }
    } catch (err) {
      message.error('Failed to load Terms and Conditions');
    } finally {
      setTermsLoading(false);
    }
  };

  const fetchPrivacy = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'legal_content', 'privacy_policy'));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPrivacyTitle(data.title || 'Privacy Policy');
        setPrivacyContent(data.content || DEFAULT_PRIVACY);
      } else {
        setPrivacyContent(DEFAULT_PRIVACY);
      }
    } catch (err) {
      message.error('Failed to load Privacy Policy');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const fetchSafety = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'legal_content', 'safety_and_privacy'));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSafetyTitle(data.title || 'Safety & Privacy');
        setSafetyContent(data.content || DEFAULT_SAFETY);
      } else {
        setSafetyContent(DEFAULT_SAFETY);
      }
    } catch (err) {
      message.error('Failed to load Safety & Privacy');
    } finally {
      setSafetyLoading(false);
    }
  };

  const saveTerms = async () => {
    if (!termsContent.trim()) {
      message.warning('Content cannot be empty');
      return;
    }
    setTermsSaving(true);
    try {
      await setDoc(doc(db, 'legal_content', 'terms_and_conditions'), {
        title: termsTitle,
        content: termsContent,
        lastUpdated: serverTimestamp()
      });
      logActivity('update_terms_and_conditions', 'Updated Terms and Conditions');
      message.success('Terms and Conditions saved successfully');
    } catch (err) {
      message.error('Failed to save Terms and Conditions');
    } finally {
      setTermsSaving(false);
    }
  };

  const savePrivacy = async () => {
    if (!privacyContent.trim()) {
      message.warning('Content cannot be empty');
      return;
    }
    setPrivacySaving(true);
    try {
      await setDoc(doc(db, 'legal_content', 'privacy_policy'), {
        title: privacyTitle,
        content: privacyContent,
        lastUpdated: serverTimestamp()
      });
      logActivity('update_privacy_policy', 'Updated Privacy Policy');
      message.success('Privacy Policy saved successfully');
    } catch (err) {
      message.error('Failed to save Privacy Policy');
    } finally {
      setPrivacySaving(false);
    }
  };

  const saveSafety = async () => {
    if (!safetyContent.trim()) {
      message.warning('Content cannot be empty');
      return;
    }
    setSafetySaving(true);
    try {
      await setDoc(doc(db, 'legal_content', 'safety_and_privacy'), {
        title: safetyTitle,
        content: safetyContent,
        lastUpdated: serverTimestamp()
      });
      logActivity('update_safety_and_privacy', 'Updated Safety & Privacy');
      message.success('Safety & Privacy saved successfully');
    } catch (err) {
      message.error('Failed to save Safety & Privacy');
    } finally {
      setSafetySaving(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Legal Content Management</h1>
        <p className="text-gray-500 mt-1">
          Edit Terms and Conditions and Privacy Policy. Changes will reflect immediately on the public pages.
        </p>
      </div>

      <Tabs defaultActiveKey="terms" size="large">
        {/* Terms and Conditions Tab */}
        <TabPane
          tab={
            <span className="flex items-center gap-2">
              <FileTextOutlined />
              Terms and Conditions
            </span>
          }
          key="terms"
        >
          <Card className="rounded-xl shadow-sm">
            {termsLoading ? (
              <div className="flex justify-center py-16">
                <Spin size="large" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Page Title</label>
                  <Input
                    value={termsTitle}
                    onChange={(e) => setTermsTitle(e.target.value)}
                    placeholder="Terms and Conditions"
                    size="large"
                    className="rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Content</label>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden', background: '#fafafa' }}>
                    <textarea
                      value={termsContent}
                      onChange={(e) => setTermsContent(e.target.value)}
                      placeholder="Write your Terms and Conditions here..."
                      rows={24}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '14px',
                        lineHeight: '1.75',
                        fontFamily: 'inherit',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        resize: 'vertical',
                        color: '#374151',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{termsContent.length} characters</p>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 text-sm hover:underline"
                  >
                    Preview public page →
                  </a>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={termsSaving}
                    onClick={saveTerms}
                    style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', border: 'none' }}
                    className="rounded-lg px-6"
                  >
                    Save Terms and Conditions
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabPane>

        {/* Privacy Policy Tab */}
        <TabPane
          tab={
            <span className="flex items-center gap-2">
              <SafetyOutlined />
              Privacy Policy
            </span>
          }
          key="privacy"
        >
          <Card className="rounded-xl shadow-sm">
            {privacyLoading ? (
              <div className="flex justify-center py-16">
                <Spin size="large" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Page Title</label>
                  <Input
                    value={privacyTitle}
                    onChange={(e) => setPrivacyTitle(e.target.value)}
                    placeholder="Privacy Policy"
                    size="large"
                    className="rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Content</label>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden', background: '#fafafa' }}>
                    <textarea
                      value={privacyContent}
                      onChange={(e) => setPrivacyContent(e.target.value)}
                      placeholder="Write your Privacy Policy here..."
                      rows={24}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '14px',
                        lineHeight: '1.75',
                        fontFamily: 'inherit',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        resize: 'vertical',
                        color: '#374151',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{privacyContent.length} characters</p>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 text-sm hover:underline"
                  >
                    Preview public page →
                  </a>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={privacySaving}
                    onClick={savePrivacy}
                    style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', border: 'none' }}
                    className="rounded-lg px-6"
                  >
                    Save Privacy Policy
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabPane>

        {/* Safety & Privacy Tab */}
        <TabPane
          tab={
            <span className="flex items-center gap-2">
              <SafetyCertificateOutlined />
              Safety &amp; Privacy
            </span>
          }
          key="safety"
        >
          <Card className="rounded-xl shadow-sm">
            {safetyLoading ? (
              <div className="flex justify-center py-16">
                <Spin size="large" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Page Title</label>
                  <Input
                    value={safetyTitle}
                    onChange={(e) => setSafetyTitle(e.target.value)}
                    placeholder="Safety & Privacy"
                    size="large"
                    className="rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Content</label>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden', background: '#fafafa' }}>
                    <textarea
                      value={safetyContent}
                      onChange={(e) => setSafetyContent(e.target.value)}
                      placeholder="Write your Safety & Privacy content here..."
                      rows={24}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '14px',
                        lineHeight: '1.75',
                        fontFamily: 'inherit',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        resize: 'vertical',
                        color: '#374151',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{safetyContent.length} characters</p>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <a
                    href="/safety-and-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 text-sm hover:underline"
                  >
                    Preview public page →
                  </a>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={safetySaving}
                    onClick={saveSafety}
                    style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', border: 'none' }}
                    className="rounded-lg px-6"
                  >
                    Save Safety &amp; Privacy
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default LegalContentManagement;
